import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api";
import { fdt } from "../api";
import { Radar, Bell, Menu, LogOut } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import RadarAI from "./RadarAI";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user, location.pathname]);

  const dashPath = user?.role === "admin" ? "/control" : user?.role === "company" ? "/company" : "/courier";

  const links = [];
  if (!user) {
    links.push(["/shippers", "For Shippers", "nav-shippers"],
      ["/carriers", "For Carriers", "nav-carriers"],
      ["/pricing", "Pricing", "nav-pricing"]);
  } else if (user.role === "courier") {
    links.push(["/pricing", "Pricing", "nav-pricing"],
      [dashPath, "Dashboard", "nav-dashboard"]);
  } else if (user.role === "company") {
    links.push(["/pricing", "Pricing", "nav-pricing"],
      [dashPath, "Dashboard", "nav-dashboard"]);
  } else if (user.role === "admin") {
    links.push(["/control", "Control Tower", "nav-control"]);
  }

  const isActive = (to) => location.pathname === to.split("?")[0];

  const navLinks = (prefix = "") => links.map(([to, label, tid]) => (
    <Link key={prefix + to} to={to} data-testid={prefix + tid} onClick={() => setMenuOpen(false)}
      className={isActive(to)
        ? "bg-[#1C1B1A] text-white px-4 py-1.5 rounded-full text-sm font-medium transition-all shadow-sm"
        : "text-[#78756E] hover:text-[#1C1B1A] px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors"}>
      {label}
    </Link>
  ));

  return (
    <div className="min-h-screen bg-white text-[#1C1B1A] flex flex-col">
      <header className="group fixed top-3 left-1/2 -translate-x-1/2 z-40 w-[94%] max-w-6xl bg-white/75 backdrop-blur-xl border border-white/60 rounded-full shadow-[0_10px_30px_rgba(28,27,26,0.08)]">
        <div className="px-4 sm:px-5 h-13 py-2.5 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <img src="/favicon.png" alt="" className="w-7 h-7 rounded-full" />
            <span className="font-heading font-bold text-base tracking-tight">CourierRadar</span>
          </Link>

          {/* Desktop: always visible */}
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks()}
          </nav>

          <div className="flex items-center gap-1.5">
            {user ? (
              <>
                <Popover onOpenChange={(open) => {
                  if (open && notifs.unread > 0) {
                    api.post("/notifications/read-all").catch(() => {});
                    setNotifs({ ...notifs, unread: 0, items: notifs.items.map((i) => ({ ...i, read: true })) });
                  }
                }}>
                  <PopoverTrigger asChild>
                    <button data-testid="notifications-bell" className="relative w-9 h-9 rounded-full hover:bg-[#F6F3EA] flex items-center justify-center transition-colors">
                      <Bell className="w-4 h-4 text-[#78756E]" />
                      {notifs.unread > 0 && (
                        <span data-testid="notifications-badge" className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#F2D06B] text-[#1C1B1A] text-[9px] font-bold flex items-center justify-center">
                          {notifs.unread}
                        </span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-80 p-0 rounded-[24px] border-[#E8E4D9]">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8E4D9]/60">
                      <span className="text-sm font-semibold">Notifications</span>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifs.items.length === 0 && <p className="text-xs text-[#A09D95] p-4">No notifications yet.</p>}
                      {notifs.items.slice(0, 20).map((n) => (
                        <button key={n.id} data-testid={`notification-${n.id}`}
                          onClick={() => {
                            api.post(`/notifications/${n.id}/read`);
                            if (!n.link) return;
                            const internal = n.link.startsWith(window.location.origin) ? n.link.slice(window.location.origin.length) : n.link;
                            if (internal.startsWith("/")) navigate(internal);
                            else window.location.href = n.link;
                          }}
                          className={`w-full text-left px-4 py-3 border-b border-[#F6F3EA] hover:bg-[#FCFAF5] transition-colors ${!n.read ? "bg-[#F6F3EA]" : ""}`}>
                          <p className="text-xs font-semibold text-[#1C1B1A]">{n.title}</p>
                          <p className="text-xs text-[#78756E] mt-0.5 line-clamp-2">{n.body}</p>
                          <p className="text-[10px] text-[#A09D95] mt-1">{fdt(n.created_at)}</p>
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Popover>
                  <PopoverTrigger asChild>
                    <button data-testid="account-menu-btn" className="w-8 h-8 rounded-full bg-[#1C1B1A] text-[#F2D06B] flex items-center justify-center text-xs font-bold hover:bg-[#2C2B2A] transition-colors">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-56 p-1.5 rounded-[20px] border-[#E8E4D9]" data-testid="account-menu">
                    <div className="px-3 py-2.5 border-b border-[#F6F3EA] mb-1">
                      <p className="text-xs font-semibold truncate">{user.name}</p>
                      <p className="text-[10px] text-[#A09D95] truncate">{user.email}</p>
                      {user.subscription_plan && <span className="cr-pill-yellow !text-[9px] mt-1 inline-block">{user.subscription_plan}</span>}
                    </div>
                    <Link to={dashPath} data-testid="menu-dashboard" className="block px-3 py-2 text-xs text-[#1C1B1A] hover:bg-[#F6F3EA] rounded-xl transition-colors">Dashboard</Link>
                    <Link to="/account" data-testid="menu-account" className="block px-3 py-2 text-xs text-[#1C1B1A] hover:bg-[#F6F3EA] rounded-xl transition-colors">Account & settings</Link>
                    {user.role !== "admin" && <Link to="/account" data-testid="menu-billing" className="block px-3 py-2 text-xs text-[#1C1B1A] hover:bg-[#F6F3EA] rounded-xl transition-colors">Plan & billing</Link>}
                    <Link to="/faq" data-testid="menu-help" className="block px-3 py-2 text-xs text-[#1C1B1A] hover:bg-[#F6F3EA] rounded-xl transition-colors">Help & support</Link>
                    <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }}
                      className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 rounded-xl transition-colors flex items-center gap-1.5">
                      <LogOut className="w-3.5 h-3.5" /> Log out
                    </button>
                  </PopoverContent>
                </Popover>
              </>
            ) : (
              <>
                <Link to="/auth" data-testid="login-link" className="hidden sm:block text-sm text-[#78756E] hover:text-[#1C1B1A] px-3 py-2 transition-colors">Sign in</Link>
                <Link to="/auth?mode=register" data-testid="get-started-btn" className="cr-btn-dark !py-2 !px-4 text-xs sm:text-sm whitespace-nowrap">Get started</Link>
              </>
            )}
            {/* Mobile: menu via tap → bottom sheet */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" className="md:hidden w-9 h-9 rounded-full hover:bg-[#F6F3EA] flex items-center justify-center">
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="bottom" className="rounded-t-[28px] border-[#E8E4D9] bg-[#FCFAF5]">
                <div className="w-10 h-1 bg-[#E8E4D9] rounded-full mx-auto mb-6" />
                <div className="flex flex-col gap-4 pb-8 px-1">
                  {navLinks("m-")}
                  {user && <Link to="/account" data-testid="m-nav-account" onClick={() => setMenuOpen(false)} className="text-sm text-[#78756E] px-3.5">Account</Link>}
                  {!user && <Link to="/track" data-testid="m-nav-track" onClick={() => setMenuOpen(false)} className="text-sm text-[#78756E] px-3.5">Track a delivery</Link>}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
      <main className="flex-1 pt-16">{children}</main>
      <RadarAI />
      <footer className="mt-16 border-t border-[#E8E4D9]/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 sm:grid-cols-5 gap-8 text-xs">
          <div>
            <span className="font-heading font-bold text-sm text-[#1C1B1A] block mb-3">CourierRadar</span>
            <p className="text-[#A09D95] leading-relaxed">Transport work, settled. The courier marketplace for the UK and Europe.</p>
            <a href="mailto:office@courierRadar.com" className="block mt-3 text-[#78756E] hover:text-[#1C1B1A]">office@courierRadar.com</a>
          </div>
          <nav className="flex flex-col gap-2" data-testid="footer-product">
            <span className="cr-label mb-1">Product</span>
            <Link to="/services" className="text-[#78756E] hover:text-[#1C1B1A]">Services</Link>
            <Link to="/platform" className="text-[#78756E] hover:text-[#1C1B1A]">Platform</Link>
            <Link to="/ai" className="text-[#78756E] hover:text-[#1C1B1A]">Radar AI</Link>
            <Link to="/pricing" className="text-[#78756E] hover:text-[#1C1B1A]">Pricing</Link>
          </nav>
          <nav className="flex flex-col gap-2" data-testid="footer-work">
            <span className="cr-label mb-1">Work</span>
            <Link to="/find-jobs" className="text-[#78756E] hover:text-[#1C1B1A]">Find Jobs</Link>
            <Link to="/courier-jobs" className="text-[#78756E] hover:text-[#1C1B1A]">Courier jobs in Europe</Link>
            <Link to="/track" className="text-[#78756E] hover:text-[#1C1B1A]">Track a delivery</Link>
          </nav>
          <nav className="flex flex-col gap-2" data-testid="footer-resources">
            <span className="cr-label mb-1">Transport Resources</span>
            <Link to="/resources/news" className="text-[#78756E] hover:text-[#1C1B1A]">Transport News</Link>
            <Link to="/resources/road-alerts" className="text-[#78756E] hover:text-[#1C1B1A]">Road Alerts</Link>
            <Link to="/resources/fuel-prices" className="text-[#78756E] hover:text-[#1C1B1A]">Fuel Prices</Link>
            <Link to="/resources/driver-rules" className="text-[#78756E] hover:text-[#1C1B1A]">Driver Rules</Link>
          </nav>
          <nav className="flex flex-col gap-2" data-testid="footer-company">
            <span className="cr-label mb-1">Company</span>
            <button type="button" data-testid="footer-tour-btn" onClick={() => window.dispatchEvent(new CustomEvent("cr-open-tour"))}
              className="text-left text-[#78756E] hover:text-[#1C1B1A]">Platform tour</button>
            <Link to="/faq" className="text-[#78756E] hover:text-[#1C1B1A]">Help & FAQ</Link>
            <Link to="/shippers" className="text-[#78756E] hover:text-[#1C1B1A]">For shippers</Link>
            <Link to="/carriers" className="text-[#78756E] hover:text-[#1C1B1A]">For carriers</Link>
            <Link to="/p/privacy" className="text-[#78756E] hover:text-[#1C1B1A]">Privacy</Link>
            <Link to="/p/terms" className="text-[#78756E] hover:text-[#1C1B1A]">Terms</Link>
            <Link to="/p/gdpr" className="text-[#78756E] hover:text-[#1C1B1A]">GDPR & data</Link>
          </nav>
        </div>
        <div className="py-3 text-center text-[10px] text-[#A09D95] border-t border-[#E8E4D9]/50">© {new Date().getFullYear()} CourierRadar · courierradar.com</div>
      </footer>
    </div>
  );
}
